import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

const DEFAULT_ROLES = {
  worship: ['Soprano', 'Contralto', 'Tenor', 'Barítono', 'Bateria', 'Baixo', 'Guitarra', 'Violão', 'Teclado'],
  media: ['Câmera', 'Edição', 'Transmissão', 'Som', 'Iluminação', 'Projeção'],
  dance: ['Dançar'],
  reception: ['Receber', 'Acolher', 'Conectar', 'Servir', 'Orientar', 'Pegar Dados'],
  kids: ['Professor', 'Ajudante', 'Louvor', 'Teatro'],
};

const storageKey = (team) => `mbv_schedule_roles_${team}_v1`;

function readLocalRoles(team) {
  const defaults = (DEFAULT_ROLES[team] || []).map((name, index) => ({ id: `local-default-${index}`, name, sort_order: index }));
  if (typeof window === 'undefined') return defaults;
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey(team)) || 'null');
    return Array.isArray(stored) && stored.length ? stored : defaults;
  } catch {
    return defaults;
  }
}

export function useScheduleRoles(team) {
  const queryClient = useQueryClient();
  const [localRoles, setLocalRoles] = useState(() => readLocalRoles(team));
  const { data: remoteRoles, isError } = useQuery({
    queryKey: ['scheduleRoles', team],
    queryFn: () => base44.entities.ScheduleRole.filter({ team }, 'created_date', 200),
    retry: false,
  });

  const usesLocalStorage = isError || !remoteRoles?.length;
  const roles = useMemo(
    () => (usesLocalStorage ? localRoles : [...remoteRoles].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))),
    [usesLocalStorage, localRoles, remoteRoles],
  );

  const saveRole = async ({ id, name }) => {
    const cleaned = String(name || '').trim().replace(/\s+/g, ' ');
    if (!cleaned) throw new Error('Digite o nome da função.');
    if (roles.some((role) => role.id !== id && role.name.localeCompare(cleaned, 'pt-BR', { sensitivity: 'base' }) === 0)) {
      throw new Error('Essa função já existe.');
    }

    if (usesLocalStorage || String(id || '').startsWith('local-')) {
      const next = id
        ? roles.map((role) => role.id === id ? { ...role, name: cleaned } : role)
        : [...roles, { id: `local-${crypto.randomUUID()}`, name: cleaned, sort_order: roles.length }];
      window.localStorage.setItem(storageKey(team), JSON.stringify(next));
      setLocalRoles(next);
      return 'local';
    }

    if (id) {
      await base44.entities.ScheduleRole.update(id, { name: cleaned });
    } else {
      await base44.entities.ScheduleRole.create({ team, name: cleaned, sort_order: roles.length });
    }
    await queryClient.invalidateQueries({ queryKey: ['scheduleRoles', team] });
    return 'shared';
  };

  return { roles, saveRole };
}
